import { cloneElement, PureComponent } from 'react';

import { debounce } from 'lodash-es';
import { FormattedMessage } from 'react-intl';

import Spinner from '@atlaskit/spinner';

import { Ref, RefType } from 'src/sections/repository/types';

import * as styles from './ref-list.style';
import messages from './ref-selector.i18n';

const ADDITIONAL_ELEMENT_CLIENT_HEIGHT = 100;

type RefListProps = {
  nextPageUrl: string | null | undefined;
  isLoading: boolean;
  onLoadMorePages: (nextPageUrl: string | null | undefined) => void;
  onRefSelected: (ref: Ref) => void;
  refs: Ref[];
  renderRef: (ref: Ref) => any;
  type: RefType;
  isFiltered: boolean;
};

export default class RefList extends PureComponent<RefListProps> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    hasMore: false,
    isLoading: false,
    onLoadMorePages: () => {},
    onRefSelected: () => {},
    refs: [],
    renderRef: (ref: Ref) => (
      <styles.RefButton appearance="link">{ref.name}</styles.RefButton>
    ),
  };

  renderContents() {
    const { refs, type, isFiltered } = this.props;

    if (type === 'tag' && refs.length === 0) {
      return isFiltered ? (
        <FormattedMessage {...messages.noResultsTags} />
      ) : (
        <FormattedMessage {...messages.tagsEmptyState} />
      );
    }
    if (type === 'branch' && refs.length === 0) {
      return <FormattedMessage {...messages.noResultsBranches} />;
    }

    return refs.map((ref: Ref, index: number) => (
      <styles.RefListItem key={index}>{this.renderRef(ref)}</styles.RefListItem>
    ));
  }

  renderRef = (ref: Ref) => {
    const refElement = this.props.renderRef(ref);
    return cloneElement(refElement, {
      key: ref.name,
      onClick: e => {
        if (refElement.props.onClick) {
          refElement.props.onClick(e);
        }
        this.props.onRefSelected(ref);
      },
    });
  };

  onLoadMoreScrollHandler = debounce(e => {
    const element = e.target;
    const { nextPageUrl, isLoading, onLoadMorePages } = this.props;
    if (
      nextPageUrl &&
      !isLoading &&
      // add ADDITIONAL_ELEMENT_CLIENT_HEIGHT to clientHeight so that the user cannot see the space reserved for the Spinner Container
      // when scrolling at a slow pace
      element.scrollHeight - element.scrollTop <
        element.clientHeight + ADDITIONAL_ELEMENT_CLIENT_HEIGHT
    ) {
      onLoadMorePages(nextPageUrl);
    }
  }, 200);

  render() {
    const { isLoading, refs } = this.props;
    if (isLoading && (!refs || refs.length === 0)) {
      return (
        <styles.LoadingContainer>
          <Spinner size="medium" testId="spinner" />
        </styles.LoadingContainer>
      );
    }

    return (
      <styles.ScrollableContents
        onScroll={e => {
          this.onLoadMoreScrollHandler(e);
        }}
      >
        <styles.RefList role="list">{this.renderContents()}</styles.RefList>
        <styles.SpinnerContainer>
          {isLoading && <Spinner size="medium" />}
        </styles.SpinnerContainer>
      </styles.ScrollableContents>
    );
  }
}
